'use client'

import type { ToolFailure } from '@/lib/tool-generations'

// Drives every closed-loop tool page (/tools/*). One hook, one backend, one
// flow — the generalization of use-seedance-2-5 / use-seedance-mini:
//
//   1. loadSpec()        — GET /cost. The server owns the controls, the engine
//                          name and the price, so page copy can't drift from
//                          what actually runs.
//   2. ensureWorkspace() — a brand-new signup has no org, so no credits exist
//                          to check. Silently create a real self-auth org +
//                          incomplete brand, then activate it (setActiveOrg +
//                          force-refresh) so org-scoped calls resolve against
//                          it. Users who already have an org skip this.
//   3. uploadFile()      — presigned PUT straight to fal storage. The edit
//                          tools take real files; routing a 50MB clip through
//                          a data URL is not an option.
//   4. generate()        — POST /generate. 402 means the page opens the upgrade
//                          dialog; otherwise it returns the fal request id.
//   5. pollStatus()      — poll the key-backed proxy until the output URL lands.
//
// The backend (apps/api/routers/landing/_fal_tool.py) is authoritative for cost
// and for the credit gate; nothing here is trusted for either.

import { useCallback, useRef } from 'react'

import { useAuthContext } from '@/hooks/use-auth-context'
import { getApiBaseUrl } from '@/lib/api-config'
import {
  apiErrorCode,
  apiErrorMessage,
  apiErrorNumber,
} from '@/lib/api-error-body'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import {
  fetchPreviewEligibility,
  type PreviewEligibility,
} from '@/lib/free-preview'
import { isWorkspaceSelectionRequired } from '@/lib/tool-workspace-selection'

export type FalToolInput =
  | 'prompt'
  | 'duration'
  | 'resolution'
  | 'aspect_ratio'
  | 'generate_audio'
  | 'seed'
  | 'image_urls'
  | 'video_urls'
  | 'audio_urls'
  | 'voice'
  | 'language'
  | 'source_url'

export type FalOutputKind = 'video' | 'image' | 'audio' | 'text'

/** One closed set of named options, as the server declares it. */
export interface FalToolChoice {
  key: string
  label: string
  options: string[]
  default: string
}

/** The server's description of one tool: controls, limits and price. */
export interface FalToolSpec {
  slug: string
  engine: string
  outputKind: FalOutputKind
  inputs: FalToolInput[]
  required: FalToolInput[]
  resolutions: string[]
  /** Ratios this model actually accepts; empty means the studio's default set. */
  aspectRatios: string[]
  defaultResolution: string | null
  minDuration: number | null
  maxDuration: number | null
  /**
   * The exact durations the model accepts, when they are not every integer in
   * [min, max]. Empty for the contiguous models. Veo takes 4/6/8 and Luma takes
   * 5/10, so rendering the range would offer values fal rejects — after the job
   * has been charged for.
   */
  durations: number[]
  defaultDuration: number | null
  creditsPerSecond: Record<string, number> | null
  flatCredits: number | null
  accepts: string[]
  maxSourceSeconds: number | null
  /** Presign ceiling in bytes, so an over-sized file can be named as such. */
  maxUploadBytes: number
  maxImageUrls: number
  minImageUrls: number
  maxVideoUrls: number
  maxAudioUrls: number
  voices: string[]
  defaultVoice: string | null
  /** Closed set of target markets, for tools whose only variable is language. */
  languages: string[]
  defaultLanguage: string | null
  /**
   * What the language control is asking for. Empty means "Target language",
   * which is right for localization and wrong for the subtitle tool: nothing
   * is translated there, and the pick only says which language is already
   * being spoken. The server owns the wording so the label cannot drift from
   * what the pick actually does.
   */
  languageLabel: string
  /**
   * Extra closed-enum controls the server declares — a lettering weight, a
   * highlight colour. Served rather than listed in this bundle because each
   * value reaches the provider, and one it does not know is a job that is
   * charged for and then fails.
   */
  choices: FalToolChoice[]
  /** Label and placeholder for the `source_url` box; the field is generic, what
   *  it means is per-tool ("Listing URL", "Product URL"). */
  sourceUrlLabel: string
  sourceUrlPlaceholder: string
  /**
   * Fields of which AT LEAST ONE must be present — an OR, unlike `required`.
   * The real-estate tool takes a listing URL *or* listing photos and works from
   * either, so gating on both would refuse the paste-a-link flow it is built on.
   */
  requireAny: string[]
  requireAnyMessage: string
  /** Preselected ratio; without one the studio falls back to the first listed. */
  defaultAspectRatio: string | null
  /**
   * Preselect the ratio of the uploaded clip rather than a fixed default.
   * Set on tools meant to preserve the shot — a default is wrong half the time
   * there, and being wrong costs a paid render.
   */
  followSourceAspectRatio: boolean
  /**
   * Preselect the uploaded clip's RUNTIME rather than a fixed default.
   * A localization model fits the whole script into the runtime it is given,
   * so defaulting a 13s ad to 5s does not truncate it — it crushes the speech
   * into gibberish, at full price.
   */
  followSourceDuration: boolean
  notes: string
}

export interface FalToolWorkspace {
  orgId: string
  authOrgId: string
  orgSlug: string
  brandId: string
  brandSlug: string
}

export interface GenerateParams {
  workspaceId?: string
  prompt?: string
  duration?: number
  resolution?: string
  aspectRatio?: string
  generateAudio?: boolean
  imageUrls?: string[]
  videoUrls?: string[]
  audioUrls?: string[]
  voice?: string
  language?: string
  seed?: number
  /** Answers to the spec's `choices`, keyed as the server declared them. */
  choices?: Record<string, string>
  sourceUrl?: string
}

export type GenerateResult =
  | {
      status: 'ok'
      requestId: string
      creditsCharged: number
      balanceAfter: number
    }
  | { status: 'insufficient'; needed?: number; balance?: number }
  | { status: 'plan_required'; requiredPlan: 'creator' }
  | { status: 'workspace_selection_required' }
  | { status: 'unauthenticated' }
  | { status: 'invalid'; message: string }
  | { status: 'error'; message: string }

export interface HandoffResult {
  projectId: string
  orgSlug: string
  brandSlug: string
  /** In-product path to the seeded Creatives canvas. */
  path: string
}

export interface PollResult {
  failure?: ToolFailure | null
  status: string
  /** `preview` on the org's free watermarked run; see CreditBalance.tool_generation_mode. */
  deliveryMode?: 'full' | 'preview' | null
  outputUrl: string | null
  /** Text tools return their result inline — there is no media URL. */
  outputText?: string | null
  outputKind: FalOutputKind | null
  queuePosition?: number | null
}

/** Estimated cost for the current controls. Display only — the server re-checks
 *  and is the only thing that decides what gets charged. */
export function computeCreditCost(
  spec: FalToolSpec | null,
  opts: { duration?: number; resolution?: string },
): number {
  if (!spec) return 0
  if (spec.creditsPerSecond) {
    const table = spec.creditsPerSecond
    const perSec =
      table[opts.resolution ?? ''] ??
      table[spec.defaultResolution ?? ''] ??
      Object.values(table)[0] ??
      0
    const seconds =
      opts.duration ?? spec.defaultDuration ?? spec.minDuration ?? 5
    return Math.ceil(seconds * perSec)
  }
  return spec.flatCredits ?? 0
}

/** One sessionStorage slot per tool — two tool pages open at once must not
 *  overwrite each other's cached workspace. */
const workspaceKey = (slug: string) => `fal_tool_workspace:${slug}`

interface StoredWorkspace {
  workspace: FalToolWorkspace
  userId: string | null
}

function readStoredWorkspace(slug: string): StoredWorkspace | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(workspaceKey(slug))
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredWorkspace
    return stored?.workspace?.orgId ? stored : null
  } catch {
    return null
  }
}

/**
 * Specs already fetched this page-load, by slug.
 *
 * A spec is immutable server-owned config, so a second fetch for the same slug
 * can only ever return the same answer — and on a page whose two media modes
 * are two slugs, that second fetch is what the visitor sees as a flash: the
 * studio unmounts, remounts into `specState: 'loading'`, and the whole control
 * column collapses to a skeleton before coming back. Serving the cached spec
 * synchronously means the switched-to mode renders ready on its first frame.
 */
const specCache = new Map<string, FalToolSpec>()

/** A spec already in hand, for a first render that skips the loading state. */
export function getCachedFalToolSpec(slug: string): FalToolSpec | null {
  return specCache.get(slug) ?? null
}

/**
 * Warm the cache for a slug the visitor has not opened yet (the other tab).
 * Fire-and-forget: a miss just means the normal load path runs.
 */
export function prefetchFalToolSpec(slug: string): void {
  if (specCache.has(slug)) return
  // web-arch-allow-direct-fetch: same public spec bootstrap as `loadSpec`.
  void fetch(`${getApiBaseUrl()}/api/tools/fal/${slug}/cost`)
    .then(res => (res.ok ? res.json() : null))
    .then((body: { data?: FalToolSpec } | null) => {
      if (body?.data) specCache.set(slug, body.data)
    })
    .catch(() => {
      // Prefetch is an optimisation; the mount-time load still runs.
    })
}

/**
 * An upload that failed, carrying the presign status that explains WHY.
 *
 * Without the status every failure collapsed into "Upload failed. Please try a
 * different file." — including the 422 that means the file is over the presign
 * ceiling, which sends people looking for a format problem instead of
 * compressing the clip.
 */
export class UploadFailure extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
  ) {
    super(message)
    this.name = 'UploadFailure'
  }
}

/**
 * How long the transfer may go without a single progress event before we call
 * it dead. Generous on purpose: this fires on genuinely stalled sockets, not on
 * slow ones — a large ad video over a hotel connection still ticks.
 */
const UPLOAD_STALL_MS = 45_000

/**
 * PUT the file with progress, via XHR rather than `fetch`.
 *
 * `fetch` cannot report upload progress, and a source ad is routinely tens of
 * megabytes — the studio was showing a motionless "Uploading…" for minutes on
 * an upload that was working fine, which is indistinguishable from a hang and
 * loses the visitor at the exact step we count as the conversion. It also had
 * no timeout, so a socket that really did die never surfaced an error at all.
 */
export function putWithProgress(
  url: string,
  file: File,
  onProgress: (sent: number, total: number) => void,
): Promise<void> {
  return putBlobWithProgress(url, file, onProgress, false).then(() => {})
}

function putBlobWithProgress(
  url: string,
  body: Blob,
  onProgress: (sent: number, total: number) => void,
  multipart: boolean,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let stallTimer: ReturnType<typeof setTimeout>
    // `abort()` dispatches its event synchronously, so `onabort` below settles
    // the promise before the timer callback can reject — a rejection after
    // `xhr.abort()` is dead code. The flag is what lets `onabort` tell a
    // watchdog abort from one the caller asked for, and it is the difference
    // between "check your connection" and "try a different file".
    let stalled = false

    const armStallTimer = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        stalled = true
        xhr.abort()
      }, UPLOAD_STALL_MS)
    }

    xhr.open('PUT', url)
    if (!multipart)
      xhr.setRequestHeader(
        'Content-Type',
        body.type || 'application/octet-stream',
      )
    xhr.upload.onprogress = event => {
      armStallTimer()
      if (event.lengthComputable) onProgress(event.loaded, event.total)
    }
    xhr.onload = () => {
      clearTimeout(stallTimer)
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new UploadFailure('upload_put_failed', xhr.status))
        return
      }
      if (!multipart) {
        resolve(null)
        return
      }
      // Fal returns the part ETag in JSON and as a header. Prefer the body,
      // since a cross-origin response may not expose the header to the browser.
      let etag: unknown
      try {
        etag = (JSON.parse(xhr.responseText) as { etag?: unknown })?.etag
      } catch {
        // Some storage proxies only carry the header.
      }
      etag = etag ?? xhr.getResponseHeader('etag')
      if (typeof etag !== 'string' || !etag) {
        reject(new UploadFailure('upload_invalid_response'))
        return
      }
      resolve(etag)
    }
    xhr.onerror = () => {
      clearTimeout(stallTimer)
      reject(new UploadFailure('upload_network_error'))
    }
    // Chrome also emits `timeout` for transport-level ERR_TIMED_OUT, even
    // without xhr.timeout. The request is already DONE, so a later watchdog
    // abort emits no abort event and cannot settle this promise.
    xhr.ontimeout = () => {
      clearTimeout(stallTimer)
      reject(new UploadFailure('upload_stalled'))
    }
    xhr.onabort = () => {
      clearTimeout(stallTimer)
      reject(new UploadFailure(stalled ? 'upload_stalled' : 'upload_aborted'))
    }
    armStallTimer()
    xhr.send(body)
  })
}

/** Total presign+PUT attempts, and the waits before attempt 2 and attempt 3. */
export const UPLOAD_MAX_ATTEMPTS = 3
export const UPLOAD_RETRY_DELAYS_MS = [1_000, 2_000] as const

/**
 * Whether a failure is worth another attempt: a dead socket, a network error,
 * or the far side being briefly unable to serve. A 4xx is a verdict on the
 * request itself — the file is too large, the signature is wrong — and repeating
 * it only makes the visitor wait longer for the same answer.
 */
export function isTransientUploadFailure(err: unknown): boolean {
  if (!(err instanceof UploadFailure)) return false
  switch (err.message) {
    case 'upload_stalled':
    case 'upload_network_error':
      return true
    case 'upload_put_failed':
      return err.status === 0 || err.status === 429 || (err.status ?? 0) >= 500
    case 'upload_presign_failed':
      return err.status === 429 || (err.status ?? 0) >= 500
    default:
      return false
  }
}

export interface PresignedUpload {
  uploadUrl: string
  fileUrl: string
  multipart?: boolean
}

interface UploadRetryHooks {
  onRetry?: (attempt: number, maxAttempts: number, cause: UploadFailure) => void
  sleep?: (ms: number) => Promise<void>
}

export interface UploadAttemptHooks extends UploadRetryHooks {
  presign: () => Promise<PresignedUpload>
  put: (uploadUrl: string, multipart: boolean) => Promise<void>
}

async function retryUploadOperation<T>(
  operation: () => Promise<T>,
  hooks: UploadRetryHooks,
  canRetry: () => boolean = () => true,
): Promise<T> {
  const sleep =
    hooks.sleep ?? ((ms: number) => new Promise(res => setTimeout(res, ms)))
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      if (
        attempt >= UPLOAD_MAX_ATTEMPTS ||
        !canRetry() ||
        !isTransientUploadFailure(error)
      )
        throw error
      hooks.onRetry?.(attempt + 1, UPLOAD_MAX_ATTEMPTS, error as UploadFailure)
      await sleep(UPLOAD_RETRY_DELAYS_MS[attempt - 1])
    }
  }
}

/**
 * Presign + PUT with bounded retries on transient failures. Every attempt
 * re-presigns: a signed URL that received half a body is not one to resume.
 *
 * One stalled socket used to cost the visitor the whole upload — the page asked
 * them to pick the file again, and a manual retry then worked.
 */
export async function uploadWithRetry(
  hooks: UploadAttemptHooks,
): Promise<string> {
  let multipart = false
  return retryUploadOperation(
    async () => {
      const { uploadUrl, fileUrl, multipart: usesParts } = await hooks.presign()
      multipart = usesParts === true
      await hooks.put(uploadUrl, multipart)
      return fileUrl
    },
    hooks,
    // Multipart retries its current part. An exhausted part must not start
    // another session and send the entire file up to three more times.
    () => !multipart,
  )
}

export const FAL_MULTIPART_CHUNK_BYTES = 10 * 1024 * 1024

/** Fal's multipart protocol: sequential parts followed by an ETag manifest. */
export async function multipartWithProgress(input: {
  uploadUrl: string
  file: File
  onProgress: (sent: number, total: number) => void
  onRetry?: UploadRetryHooks['onRetry']
  sleep?: UploadRetryHooks['sleep']
  fetchImpl?: typeof fetch
}): Promise<void> {
  const url = new URL(input.uploadUrl)
  const parts: { partNumber: number; etag: string }[] = []
  for (
    let start = 0;
    start < input.file.size;
    start += FAL_MULTIPART_CHUNK_BYTES
  ) {
    const chunk = input.file.slice(start, start + FAL_MULTIPART_CHUNK_BYTES)
    const partNumber = parts.length + 1
    const partUrl = `${url.origin}${url.pathname}/${partNumber}${url.search}`
    const etag = await retryUploadOperation(
      () =>
        putBlobWithProgress(
          partUrl,
          chunk,
          sent => {
            // Acknowledged earlier parts do not prove a retry has resumed.
            if (sent > 0)
              input.onProgress(
                start + Math.min(sent, chunk.size),
                input.file.size,
              )
          },
          true,
        ),
      input,
    )
    // Successful parts are retained across a retry of the next part.
    if (!etag) throw new UploadFailure('upload_invalid_response')
    parts.push({ partNumber, etag })
    input.onProgress(start + chunk.size, input.file.size)
  }
  let response: Response
  try {
    // web-arch-allow-direct-fetch: complete the presigned Fal source upload.
    response = await (input.fetchImpl ?? fetch)(
      `${url.origin}${url.pathname}/complete${url.search}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parts }),
        signal: AbortSignal.timeout(UPLOAD_STALL_MS),
      },
    )
  } catch (error) {
    if (error instanceof Error && error.name === 'TimeoutError')
      throw new UploadFailure('upload_stalled')
    throw new UploadFailure('upload_network_error')
  }
  if (!response.ok)
    throw new UploadFailure('upload_put_failed', response.status)
}

/**
 * Ask our API for a signed fal-storage PUT. A `fetch` that never reaches the
 * server rejects with a bare `TypeError`; that is the same dropped connection
 * the PUT watchdog catches, only earlier, so it is normalized to the same
 * transient failure and gets the same retries.
 */
export async function presignUpload(input: {
  url: string
  token: string
  file: File
  fetchImpl?: typeof fetch
}): Promise<PresignedUpload> {
  const fetchImpl = input.fetchImpl ?? fetch
  let res: Response
  try {
    // web-arch-allow-direct-fetch: imperative marketing tool upload.
    res = await fetchImpl(input.url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${input.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contentType: input.file.type || 'application/octet-stream',
        fileName: input.file.name,
        size: input.file.size,
        supportsMultipart: true,
      }),
    })
  } catch {
    throw new UploadFailure('upload_network_error')
  }
  if (!res.ok) throw new UploadFailure('upload_presign_failed', res.status)
  const body = (await res.json()) as {
    data?: PresignedUpload
  }
  if (!body.data?.uploadUrl || !body.data.fileUrl)
    throw new UploadFailure('upload_invalid_response')
  return body.data
}

export function useFalTool(slug: string) {
  const { getToken, user } = useAuthContext()
  const ensureInFlight = useRef<Promise<FalToolWorkspace> | null>(null)

  const base = useCallback(() => `${getApiBaseUrl()}/api/tools/fal`, [])

  const loadSpec = useCallback(async (): Promise<FalToolSpec | null> => {
    try {
      // web-arch-allow-direct-fetch: public marketing tool bootstrap, no auth context.
      const res = await fetch(`${base()}/${slug}/cost`)
      if (!res.ok) return null
      const body = (await res.json()) as { data?: FalToolSpec }
      if (body.data) specCache.set(slug, body.data)
      return body.data ?? null
    } catch {
      return null
    }
  }, [base, slug])

  // Create (or reuse) and activate a self-auth workspace so credits resolve.
  // Idempotent server-side; the in-flight ref collapses concurrent callers.
  const ensureWorkspace = useCallback(async (): Promise<FalToolWorkspace> => {
    const userId = user?.id ?? null
    const cached = readStoredWorkspace(slug)
    if (cached && userId && cached.userId === userId) return cached.workspace
    if (ensureInFlight.current) return ensureInFlight.current

    const run = (async () => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      // web-arch-allow-direct-fetch: imperative marketing tool bootstrap.
      const res = await fetch(`${base()}/${slug}/workspace`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: 'My workspace' }),
      })
      if (!res.ok) throw new Error(`workspace_http_${res.status}`)
      const body = (await res.json()) as { data?: FalToolWorkspace }
      if (!body.data?.orgId) throw new Error('workspace_invalid_response')

      // Activate the org so org-scoped reads (credit balance) and the generate
      // call resolve against it; the JWT otherwise lags a just-created org.
      const { tokenManager } = await import('@/lib/self-auth/token-manager')
      tokenManager.setActiveOrg(body.data.authOrgId)
      await tokenManager.getToken({ forceRefresh: true })

      sessionStorage.setItem(
        workspaceKey(slug),
        JSON.stringify({
          workspace: body.data,
          userId,
        } satisfies StoredWorkspace),
      )
      return body.data
    })()

    ensureInFlight.current = run
    try {
      return await run
    } finally {
      ensureInFlight.current = null
    }
  }, [base, getToken, slug, user])

  /**
   * The free-watermarked-preview exposure. Called once per video tool page by
   * a signed-in visitor whose plan refuses generation; the server deals the
   * experiment arm here and nowhere else. `null` = could not tell (network,
   * no workspace yet) — the caller keeps whatever the balance said.
   */
  const checkPreviewEligibility = useCallback(
    async (workspaceId: string | null): Promise<PreviewEligibility | null> => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      return fetchPreviewEligibility(
        `${base()}/${slug}/preview-eligibility`,
        token,
        workspaceId,
      )
    },
    [base, getToken, slug],
  )

  /** Presign against fal storage, PUT the file, return the public URL. */
  const uploadFile = useCallback(
    async (
      file: File,
      onProgress?: (sent: number, total: number) => void,
      onRetry?: (attempt: number, maxAttempts: number) => void,
    ): Promise<string> => {
      // The token is fetched once and reused across attempts: a retry exists to
      // outlast a dead socket, not to re-run the auth handshake.
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token) throw new Error('unauthenticated')

      return uploadWithRetry({
        presign: () => presignUpload({ url: `${base()}/upload`, token, file }),
        put: (uploadUrl, multipart) =>
          multipart
            ? multipartWithProgress({
                uploadUrl,
                file,
                onProgress: onProgress ?? (() => {}),
                onRetry,
              })
            : putWithProgress(uploadUrl, file, onProgress ?? (() => {})),
        onRetry: (attempt, maxAttempts) => {
          // The next attempt restarts the body from byte zero, so a bar left at
          // 49% for the length of the retry is reporting progress that no
          // longer exists.
          onProgress?.(0, file.size)
          onRetry?.(attempt, maxAttempts)
        },
      })
    },
    [base, getToken],
  )

  const generate = useCallback(
    async (params: GenerateParams): Promise<GenerateResult> => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token) return { status: 'unauthenticated' }
      let res: Response
      try {
        // web-arch-allow-direct-fetch: imperative generation request.
        res = await fetch(`${base()}/${slug}/generate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...(params.workspaceId ? { workspaceId: params.workspaceId } : {}),
            ...(params.prompt !== undefined ? { prompt: params.prompt } : {}),
            ...(params.duration !== undefined
              ? { duration: params.duration }
              : {}),
            ...(params.resolution !== undefined
              ? { resolution: params.resolution }
              : {}),
            ...(params.aspectRatio !== undefined
              ? { aspectRatio: params.aspectRatio }
              : {}),
            ...(params.generateAudio !== undefined
              ? { generateAudio: params.generateAudio }
              : {}),
            ...(params.imageUrls?.length
              ? { imageUrls: params.imageUrls }
              : {}),
            ...(params.videoUrls?.length
              ? { videoUrls: params.videoUrls }
              : {}),
            ...(params.audioUrls?.length
              ? { audioUrls: params.audioUrls }
              : {}),
            ...(params.voice ? { voice: params.voice } : {}),
            ...(params.language ? { language: params.language } : {}),
            // Was declared in GenerateParams and built by the studio, but never
            // reached the body — every `choices` control silently fell back to
            // its server default after the visitor had paid for their pick.
            ...(params.choices && Object.keys(params.choices).length
              ? { choices: params.choices }
              : {}),
            ...(params.sourceUrl ? { sourceUrl: params.sourceUrl } : {}),
            ...(params.seed !== undefined ? { seed: params.seed } : {}),
          }),
        })
      } catch {
        return {
          status: 'error',
          message: 'Could not connect. Check your connection and try again.',
        }
      }
      if (res.status === 402) {
        const body: unknown = await res.json().catch(() => null)
        if (apiErrorCode(body) === 'fal_video_plan_required') {
          return { status: 'plan_required', requiredPlan: 'creator' }
        }
        return {
          status: 'insufficient',
          needed: apiErrorNumber(body, 'needed'),
          balance: apiErrorNumber(body, 'balance'),
        }
      }
      if (res.status === 401) return { status: 'unauthenticated' }
      if (await isWorkspaceSelectionRequired(res)) {
        return { status: 'workspace_selection_required' }
      }
      if (res.status === 400) {
        // Spec validation (missing prompt / missing upload / bad resolution)
        // and the pre-charge source gates (too long, unreadable, wrong kind).
        // Surfaced as-is so the page can tell the visitor what to fix — the
        // server writes a sentence naming the measured duration, and the
        // fallback below is only for a body that carries no message at all.
        const body: unknown = await res.json().catch(() => null)
        return {
          status: 'invalid',
          message: apiErrorMessage(body) ?? 'Check your inputs.',
        }
      }
      if (!res.ok) {
        const errorBody: unknown = await res.json().catch(() => null)
        return {
          status: 'error',
          message:
            apiErrorMessage(errorBody) ??
            'Could not start generation. Please try again later.',
        }
      }
      const body = (await res.json()) as {
        data?: {
          requestId: string
          creditsCharged: number
          balanceAfter: number
        }
      }
      if (!body.data?.requestId)
        return {
          status: 'error',
          message: 'Could not start generation. Please try again later.',
        }
      return { status: 'ok', ...body.data }
    },
    [base, getToken, slug],
  )

  const pollStatus = useCallback(
    async (requestId: string): Promise<PollResult> => {
      const token = await getBackendAuthToken(getToken)
      // web-arch-allow-direct-fetch: imperative generation status poll.
      const res = await fetch(
        `${base()}/${slug}/generate/${encodeURIComponent(requestId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) throw new Error(`status_http_${res.status}`)
      const body = (await res.json()) as { data?: PollResult }
      return (
        body.data ?? {
          status: 'UNKNOWN',
          outputUrl: null,
          outputText: null,
          outputKind: null,
        }
      )
    },
    [base, getToken, slug],
  )

  /** Seed a Creatives project with a finished output and return where it lives.
   *  This is the step that turns a one-off tool visit into a product account
   *  with something already in it. */
  const handoff = useCallback(
    async (
      requestId: string,
      prompt: string,
      // Set by the in-product studio so the canvas lands in the brand the user
      // is standing in. Omitted on the marketing pages, where the server picks
      // the visitor's single acquisition brand — see FalToolHandoffRequest.
      brandId?: string,
      // An existing canvas to add the output to instead of seeding a new one;
      // only meaningful with `brandId`, which scopes the lookup.
      projectId?: string,
    ): Promise<HandoffResult | null> => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token) return null
      // web-arch-allow-direct-fetch: imperative marketing tool handoff.
      const res = await fetch(`${base()}/${slug}/handoff`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId,
          prompt,
          ...(brandId ? { brandId } : {}),
          ...(brandId && projectId ? { projectId } : {}),
        }),
      })
      if (!res.ok) return null
      const body = (await res.json()) as { data?: HandoffResult }
      return body.data?.path ? body.data : null
    },
    [base, getToken, slug],
  )

  return {
    loadSpec,
    ensureWorkspace,
    uploadFile,
    generate,
    pollStatus,
    handoff,
    checkPreviewEligibility,
  }
}

/**
 * This surface's permanent history reads the one shared endpoint wrapper, so a
 * field the endpoint gains cannot arrive on some tool pages and not others.
 */
export {
  historyDeliveryMode,
  loadStoredGenerations,
  type StoredGeneration,
} from '@/lib/tool-generations'
